import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, numberAttribute, OnInit, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { PatientProvider } from '../../../../care-team/models/patient-provider';
import { Team } from '../../../../care-team/models/team';
import { getPatientCareTeam } from '../../../../care-team/store/actions/care-team.actions';
import { selectCareTeamMemberById, selectIsLoading } from '../../../../care-team/store/selectors/care-team.selector';
import { loadPageWithPatientIdParameter } from '../../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../../core/store/selectors/app.selector';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../../shared/components/progress-spinner/progress-spinner.component';

@Component({
  selector: 'app-provider-location-selection',
  imports: [CommonModule, IconComponent, LetDirective, MatButtonModule, ProgressSpinnerComponent, PageHeaderComponent],
  templateUrl: './provider-location-selection.component.html',
  styleUrl: './provider-location-selection.component.scss',
})
export class ProviderLocationSelectionComponent implements OnInit {
  private store = inject(Store);
  private location = inject(Location);
  providerId!: string;
  @Input() set providerUserId(value: string) {
    this.providerId = value;
    this.patientCareTeamMember$ = this.store.select(selectCareTeamMemberById(this.providerId));
  }
  @Input({ transform: numberAttribute }) visitTypeId!: number;
  @Input() isModal: boolean = false;
  @Output() teamIdSelected = new EventEmitter<number>();

  patientCareTeamMember$!: Observable<PatientProvider | null>;
  isLoading$ = this.store.select(selectIsLoading);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);
  searchText = signal('');
  selectedTeamId = signal<number | null>(null);

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());
  }

  filterTeams(list: Team[] | null | undefined): Team[] {
    const q = this.searchText().trim().toLowerCase();
    if (!list || list.length === 0) return [];
    if (!q) return list;

    return list.filter(team => {
      const fields = [
        team.facilityName,
        team.name,
        team.facilityAddress1,
        team.facilityAddress2,
        team.facilityCity,
        team.facilityState,
        team.facilityZipCode,
      ];

      return fields.some(value => (value ?? '').toLowerCase().includes(q));
    });
  }

  getLocationLabel(team: Team): string {
    return team.facilityName ?? team.name;
  }

  getAddressLine2(team: Team): string {
    if (!team.facilityCity || !team.facilityState || !team.facilityZipCode) {
      return '';
    }

    return `${team.facilityCity}, ${team.facilityState} ${team.facilityZipCode}`;
  }

  onClearSearch(inputEl: HTMLInputElement) {
    this.searchText.set('');
    queueMicrotask(() => inputEl.focus());
  }

  onTeamActivate(teamId: number) {
    if (this.isMobileBrowser()) {
      this.selectedTeamId.set(teamId);
    } else {
      this.teamIdSelected.emit(teamId);
    }
  }

  onRowKeydown(event: KeyboardEvent, teamId: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTeamActivate(teamId);
    }
  }

  onCancel() {
    this.selectedTeamId.set(null);
    this.searchText.set('');
    this.location.back();
  }

  onContinue() {
    const teamId = this.selectedTeamId();
    if (!teamId) return;

    if (this.isMobileBrowser()) {
      this.store.dispatch(
        loadPageWithPatientIdParameter({
          route: `appointments/schedule/${this.providerId}/${this.visitTypeId}/${teamId}/appointment-selection`,
        })
      );
    } else {
      this.teamIdSelected.emit(teamId);
    }
  }

  getEmptyStateCopy(providerFound: boolean): string {
    if (!providerFound) return 'Provider Not Found';
    return this.searchText().trim().length > 0 ? 'No locations match your search' : 'No locations available';
  }
}
